import { Box, Grid, pn, Stack, useElementBreakpoints } from "@compass/core";
import { CustomFilters, Select, StudentCustomIdsPaster } from "components";
import {
  LkStudentDisplayFilterId,
  LkStudentEnrollmentDisplayFieldId,
  ReportPropertyBase,
  StudentFilter as StudentFilterModel,
  StudentFilterResource,
} from "generated/openApi";
import produce from "immer";
import { useEffect, useState } from "react";
import ApiService from "services/apiService";

export const StudentFilter = (props: {
  filter: StudentFilterModel;
  setFilter: (filter: StudentFilterModel) => void;
}) => {
  const { breakpointsRef, largeFieldBreakpoints, fullFieldBreakpoints } = useElementBreakpoints();
  const [resource, setResource] = useState<StudentFilterResource>({} as StudentFilterResource);
  const properties = resource.properties || [];
  const displayFields = resource.enrollmentDisplayFields || [];

  const gradeLevelDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.GradeLevel);
  const schoolDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.School);

  useEffect(() => {
    ApiService.students.getFilterResource().then((response) => {
      setResource(response);
    });
  }, []);

  const schoolYearField = () => {
    const propertyName = pn<StudentFilterModel>("schoolYears");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        multiple
        modelProperty={property}
        value={resource.schoolYearsWithActive?.filter((x) => props.filter.schoolYears?.includes(x.id))}
        options={resource.schoolYearsWithActive}
        getOptionLabel={(option) => option.name}
        onChange={(e, values) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.schoolYears = values.map((x) => x.id);
            })
          )
        }
      />
    );
  };

  const regionField = () => {
    const propertyName = pn<StudentFilterModel>("regionIds");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        multiple
        modelProperty={property}
        value={resource.regions?.filter((x) => props.filter.regionIds?.includes(x.id))}
        options={resource.regions}
        getOptionLabel={(option) => option.name}
        onChange={(e, values) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.regionIds = values.map((x) => x.id);
            })
          )
        }
      />
    );
  };

  const districtField = () => {
    const propertyName = pn<StudentFilterModel>("districtIds");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        multiple
        modelProperty={property}
        value={resource.districts?.filter((x) => props.filter.districtIds?.includes(x.id))}
        options={resource.districts}
        getOptionLabel={(option) => option.name}
        onChange={(e, values) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.districtIds = values.map((x) => x.id);
            })
          )
        }
      />
    );
  };

  const schoolField = () => {
    const propertyName = pn<StudentFilterModel>("schoolIds");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        label={schoolDisplayField?.displayName}
        multiple
        modelProperty={property}
        value={resource.schools?.filter((x) => props.filter.schoolIds?.includes(x.id))}
        options={resource.schools}
        getOptionLabel={(option) => option.name}
        onChange={(e, values) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.schoolIds = values.map((x) => x.id);
            })
          )
        }
      />
    );
  };

  const gradeLevelsField = () => {
    const propertyName = pn<StudentFilterModel>("gradeLevelIds");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        label={gradeLevelDisplayField?.displayName}
        multiple
        modelProperty={property}
        value={resource.gradeLevels?.filter((x) => props.filter.gradeLevelIds?.includes(x.id))}
        options={resource.gradeLevels}
        getOptionLabel={(option) => option.name}
        onChange={(e, values) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.gradeLevelIds = values.map((x) => x.id);
            })
          )
        }
      />
    );
  };

  const gradeLevelTermsField = () => {
    const propertyName = pn<StudentFilterModel>("gradeLevelTermIds");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        multiple
        modelProperty={property}
        value={resource.gradeLevelTerms?.filter((x) => props.filter.gradeLevelTermIds?.includes(x.id))}
        options={resource.gradeLevelTerms}
        getOptionLabel={(option) => option.name}
        onChange={(e, values) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.gradeLevelTermIds = values.map((x) => x.id);
            })
          )
        }
      />
    );
  };

  const endReasonTypesField = () => {
    const propertyName = pn<StudentFilterModel>("endReasonTypeIds");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        multiple
        modelProperty={property}
        value={resource.endReasonTypes?.filter((x) => props.filter.endReasonTypeIds?.includes(x.id))}
        options={resource.endReasonTypes}
        getOptionLabel={(option) => option.name}
        onChange={(e, values) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.endReasonTypeIds = values.map((x) => x.id);
            })
          )
        }
      />
    );
  };

  const studentGroupsField = () => {
    const propertyName = pn<StudentFilterModel>("studentGroupIds");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        multiple
        modelProperty={property}
        value={resource.studentGroups?.filter((x) => props.filter.studentGroupIds?.includes(x.id))}
        options={resource.studentGroups}
        getOptionLabel={(option) => option.name}
        onChange={(e, values) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.studentGroupIds = values.map((x) => x.id);
            })
          )
        }
      />
    );
  };

  const duplicateByField = () => {
    const propertyName = pn<StudentFilterModel>("duplicateByProperties");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        multiple
        modelProperty={property}
        value={resource.duplicateByProperties?.filter((x) =>
          props.filter.duplicateByProperties?.includes(x.propertyName)
        )}
        options={resource.duplicateByProperties}
        getOptionLabel={(option) => option.displayName}
        onChange={(e, values: ReportPropertyBase[]) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.duplicateByProperties = values.map((x) => x.propertyName);
            })
          )
        }
      />
    );
  };

  const programField = () => {
    const propertyName = pn<StudentFilterModel>("programIds");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        multiple
        modelProperty={property}
        value={resource.programs?.filter((x) => props.filter.programIds?.includes(x.id))}
        options={resource.programs}
        getOptionLabel={(option) => option.name}
        onChange={(e, values) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.programIds = values.map((x) => x.id);
            })
          )
        }
      />
    );
  };

  return (
    <Stack ref={breakpointsRef} spacing="lg">
      <CustomFilters
        filters={props.filter.customFilters}
        setFilters={(filters) =>
          props.setFilter(
            produce(props.filter, (draft) => {
              draft.customFilters = filters;
            })
          )
        }
        properties={resource.customFilterProperties}
      />

      <Box>
        <Grid container spacing="sm">
          <Grid item xs={largeFieldBreakpoints}>
            {schoolYearField()}
          </Grid>

          {resource.canAccessRegionFilter &&
            resource.displayFilters?.find((x) => x.id === LkStudentDisplayFilterId.Region) && (
              <Grid item xs={largeFieldBreakpoints}>
                {regionField()}
              </Grid>
            )}

          {resource.canAccessDistrictFilter &&
            resource.displayFilters?.find((x) => x.id === LkStudentDisplayFilterId.District) && (
              <Grid item xs={largeFieldBreakpoints}>
                {districtField()}
              </Grid>
            )}

          {resource.canAccessSchoolFilter && schoolDisplayField && (
            <Grid item xs={largeFieldBreakpoints}>
              {schoolField()}
            </Grid>
          )}

          {gradeLevelDisplayField && (
            <Grid item xs={largeFieldBreakpoints}>
              {gradeLevelsField()}
            </Grid>
          )}

          {resource.displayFilters?.find((x) => x.id === LkStudentDisplayFilterId.GradeLevelTerm) && (
            <Grid item xs={largeFieldBreakpoints}>
              {gradeLevelTermsField()}
            </Grid>
          )}

          <Grid item xs={largeFieldBreakpoints}>
            {endReasonTypesField()}
          </Grid>

          <Grid item xs={largeFieldBreakpoints}>
            {studentGroupsField()}
          </Grid>

          <Grid item xs={largeFieldBreakpoints}>
            {duplicateByField()}
          </Grid>

          {resource.displayFilters?.find((x) => x.id === LkStudentDisplayFilterId.Program) && (
            <Grid item xs={largeFieldBreakpoints}>
              {programField()}
            </Grid>
          )}

          <Grid item xs={fullFieldBreakpoints}>
            <StudentCustomIdsPaster
              value={props.filter.customIds}
              typeId={props.filter.customIdTypeId}
              onChange={(customIdTypeId, customIds) => {
                props.setFilter(
                  produce(props.filter, (draft) => {
                    draft.customIdTypeId = customIdTypeId;
                    draft.customIds = customIds;
                  })
                );
              }}
            />
          </Grid>
        </Grid>
      </Box>
    </Stack>
  );
};
